/**
 * NotificationsPage
 * Complete notification management page
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellOff, CheckCircle, Filter, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { notificationService } from '@/services/notification-service';
import {
  Notification,
  NotificationFilters,
  NotificationPriority,
  NotificationType,
  NOTIFICATION_PRIORITY_LABELS,
  NOTIFICATION_TYPE_LABELS
} from '@/types/notification';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { cn } from '@/utils/cn';
import toast from '@/lib/toast';

function StatCard({ label, value, valueClassName }: { label: string; value: number; valueClassName?: string }) {
  return (
    <Card size="sm">
      <CardContent className="flex flex-col items-center gap-0.5 text-center">
        <span className={cn('text-2xl font-semibold tabular-nums', valueClassName)}>{value}</span>
        <span className="text-xs text-muted-foreground sm:text-sm">{label}</span>
      </CardContent>
    </Card>
  );
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<NotificationFilters>({
    archived: false
  });
  const requestSeqRef = useRef(0);

  const loadNotifications = useCallback(async (pageToLoad = page) => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    try {
      setLoading(true);
      const response = await notificationService.getAll(filters, { page: pageToLoad, limit: 20 });

      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      if (pageToLoad === 1) {
        setNotifications(response.data.notifications);
      } else {
        setNotifications(prev => {
          const seen = new Set(prev.map(notification => notification.id));
          return [
            ...prev,
            ...response.data.notifications.filter(notification => !seen.has(notification.id))
          ];
        });
      }

      setHasMore(response.data.pagination.hasNext);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [filters, page]);

  useEffect(() => {
    loadNotifications(page);
  }, [loadNotifications, page]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await notificationService.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notificação excluída');
    } catch (error) {
      toast.error('Erro ao excluir notificação');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const handleRefresh = () => {
    if (page === 1) {
      loadNotifications(1);
    } else {
      setPage(1);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleFilterChange = (newFilters: Partial<NotificationFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const readCount = notifications.length - unreadCount;
  const priorityCount = notifications.filter(n => n.priority === 'urgent' || n.priority === 'high').length;
  const hasActiveFilters = filters.read !== undefined || Boolean(filters.priorities) || Boolean(filters.types);

  const clearFilters = () => {
    setFilters({ archived: false });
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Bell className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Notificações</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} não lida${unreadCount > 1 ? 's' : ''}` : 'Todas lidas'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={showFilters ? 'secondary' : 'outline'}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Filtros"
                aria-expanded={showFilters}
              >
                <Filter />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Filtros</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="outline" size="icon" onClick={handleRefresh} aria-label="Atualizar">
                <RefreshCw />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Atualizar</TooltipContent>
          </Tooltip>

          {unreadCount > 0 && (
            <Button type="button" onClick={handleMarkAllAsRead}>
              <CheckCircle />
              <span className="hidden sm:inline">Marcar todas como lidas</span>
              <span className="sm:hidden">Marcar lidas</span>
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <Card size="sm">
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium">Filtros</h3>
              {hasActiveFilters && (
                <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="notifications-filter-status">Status</Label>
                <NativeSelect
                  id="notifications-filter-status"
                  value={filters.read === undefined ? 'all' : filters.read ? 'read' : 'unread'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange({
                      read: value === 'all' ? undefined : value === 'read'
                    });
                  }}
                >
                  <option value="all">Todas</option>
                  <option value="unread">Não lidas</option>
                  <option value="read">Lidas</option>
                </NativeSelect>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="notifications-filter-priority">Prioridade</Label>
                <NativeSelect
                  id="notifications-filter-priority"
                  value={filters.priorities?.[0] || 'all'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange({
                      priorities: value === 'all' ? undefined : [value as NotificationPriority]
                    });
                  }}
                >
                  <option value="all">Todas</option>
                  {Object.entries(NOTIFICATION_PRIORITY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </NativeSelect>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="notifications-filter-type">Tipo</Label>
                <NativeSelect
                  id="notifications-filter-type"
                  value={filters.types?.[0] || 'all'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange({
                      types: value === 'all' ? undefined : [value as NotificationType]
                    });
                  }}
                >
                  <option value="all">Todos</option>
                  {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </NativeSelect>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && page === 1 ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando notificações...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
            <BellOff className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-medium">Nenhuma notificação</h3>
            <p className="text-sm text-muted-foreground">Você não tem notificações no momento.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 py-0">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          ))}

          {hasMore && (
            <div className="flex justify-center p-4">
              <Button type="button" variant="outline" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Carregando...' : 'Carregar mais'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {notifications.length > 0 && (
        <KpiGrid cols={4} gap={3} className="grid-cols-2">
          <StatCard label="Total" value={notifications.length} />
          <StatCard label="Não lidas" value={unreadCount} valueClassName="text-primary" />
          <StatCard label="Lidas" value={readCount} />
          <StatCard
            label="Prioritárias"
            value={priorityCount}
            valueClassName={priorityCount > 0 ? 'text-destructive' : undefined}
          />
        </KpiGrid>
      )}
    </div>
  );
}
